import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    ArrowLeft,
    ArrowRight,
    BookOpen,
    CheckCircle,
    ChevronRight,
    FileText,
    Film,
    Image as ImageIcon,
    Link as LinkIcon,
    Lock,
    Loader,
    PanelLeftClose,
    PanelLeft,
    Play,
    Award,
} from "lucide-react";

import Swal from 'sweetalert2';

import {
    completeLearnerContentRequest,
    fetchModulePlayerRequest,
    resetModuleLearningState,
    setCurrentLearnerContent,
    startLearnerContentRequest,
    startLearnerModuleRequest,
    updateLearnerContentProgressRequest,
} from "../../../redux/actions/moduleLearningActions";

import ContentRenderer from "../../Formation/Apprenant/components/ContentRenderer";
import "./OnboardingModulePlayer.css";

/* ── Helpers ── */
const flattenContents = (sections) =>
    (sections || []).flatMap((s) => s.contents || []);

const findFirstVisibleContent = (sections) => {
    const all = flattenContents(sections);
    return all.find((c) => !c.locked) || all[0] || null;
};

const getContentTypeIcon = (type) => {
    const t = String(type || "").toUpperCase();
    if (t === "VIDEO") return "🎬";
    if (t === "PDF" || t === "DOCUMENT") return "📄";
    if (t === "ARTICLE" || t === "HTML") return "📝";
    if (t === "QUIZ") return "✍️";
    if (t === "IMAGE") return "🖼️";
    if (t === "LINK") return "🔗";
    if (t === "SCORM") return "📦";
    if (t === "EVALUATION") return "📊";
    if (t === "EVENT") return "📅";
    if (t === "CERTIFICATE") return "🏆";
    return "📄";
};

const createSessionKey = (contentId) => {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
        return crypto.randomUUID();
    }
    return `content-${contentId}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const normalizeTrackPayload = (payload = {}) => {
    if (!payload || typeof payload !== "object") return {};
    const sortedKeys = Object.keys(payload).sort();
    return sortedKeys.reduce((acc, key) => { acc[key] = payload[key]; return acc; }, {});
};

const buildTrackSignature = ({ completionPercent = 0, progressPayload = {}, timeSpentDeltaSeconds = 0 }) =>
    JSON.stringify({
        completionPercent: Number(completionPercent || 0),
        timeSpentDeltaSeconds: Number(timeSpentDeltaSeconds || 0),
        progressPayload: normalizeTrackPayload(progressPayload),
    });


export default function OnboardingModulePlayer({ moduleId, missionIndex, missionTitle, onClose, onCompleteStep }) {
    const dispatch = useDispatch();

    const {
        player,
        sections,
        loading,
        error,
        currentContentId,
        saveStatus,
        saveError,
        saveContentId,
    } = useSelector((state) => state.moduleLearning || {});

    const [sidebarOpen, setSidebarOpen] = useState(true);
    const [openSections, setOpenSections] = useState({});
    const [exiting, setExiting] = useState(false);

    const trackingSessionRef = useRef(null);
    const pendingProgressRef = useRef(null);
    const flushTimerRef = useRef(null);
    const currentContentRef = useRef(null);
    const contentProgressByIdRef = useRef(new Map());
    const lastSentProgressRef = useRef(new Map());
    const lastCompletedContentIdRef = useRef(null);
    const lastStartedContentIdRef = useRef(null);

    /* ── Fetch module data ── */
    useEffect(() => {
        if (!moduleId) return;
        dispatch(fetchModulePlayerRequest(moduleId));
        dispatch(startLearnerModuleRequest(moduleId));
        return () => dispatch(resetModuleLearningState());
    }, [dispatch, moduleId]);

    /* ── Open all sections by default ── */
    useEffect(() => {
        if (sections?.length) {
            const map = {};
            sections.forEach((s) => { map[s.id] = true; });
            setOpenSections(map);
        }
    }, [sections]);

    /* ── Tracking helpers (same logic as ModulePlayerPage) ── */
    const clearScheduledFlush = useCallback(() => {
        if (flushTimerRef.current) {
            window.clearTimeout(flushTimerRef.current);
            flushTimerRef.current = null;
        }
    }, []);

    const ensureTrackingSession = useCallback((contentId) => {
        if (!trackingSessionRef.current || String(trackingSessionRef.current.contentId) !== String(contentId)) {
            trackingSessionRef.current = { contentId, sessionKey: createSessionKey(contentId), sequenceNumber: 0 };
        }
        return trackingSessionRef.current;
    }, []);

    const buildSequencedPayload = useCallback((contentId, payload = {}) => {
        const session = ensureTrackingSession(contentId);
        session.sequenceNumber += 1;
        return { ...payload, sessionKey: session.sessionKey, clientSequenceNumber: session.sequenceNumber, clientSentAt: new Date().toISOString() };
    }, [ensureTrackingSession]);

    const flushPendingProgress = useCallback(() => {
        if (!moduleId || !pendingProgressRef.current) return;
        const pending = pendingProgressRef.current;
        const persisted = contentProgressByIdRef.current.get(String(pending.contentId)) || {};
        const isCompleted = String(persisted.status || "").toUpperCase() === "COMPLETED" || String(lastCompletedContentIdRef.current) === String(pending.contentId);
        const sameCompletion = Number(pending.completionPercent || 0) === Number(persisted.progressPercent || 0);
        const samePayload = JSON.stringify(normalizeTrackPayload(pending.progressPayload || {})) === JSON.stringify(normalizeTrackPayload(persisted.progressPayload || {}));
        const hasNoDelta = Number(pending.timeSpentDeltaSeconds || 0) <= 0;
        const signature = buildTrackSignature(pending);

        if (isCompleted || (sameCompletion && samePayload && hasNoDelta) || (hasNoDelta && lastSentProgressRef.current.get(String(pending.contentId)) === signature)) {
            pendingProgressRef.current = null;
            clearScheduledFlush();
            return;
        }
        pendingProgressRef.current = null;
        clearScheduledFlush();
        lastSentProgressRef.current.set(String(pending.contentId), signature);
        dispatch(updateLearnerContentProgressRequest(moduleId, pending.contentId, buildSequencedPayload(pending.contentId, {
            timeSpentDeltaSeconds: pending.timeSpentDeltaSeconds || 0,
            completionPercent: pending.completionPercent || 0,
            progressPayload: pending.progressPayload || {},
        })));
    }, [buildSequencedPayload, clearScheduledFlush, dispatch, moduleId]);

    const discardPendingProgress = useCallback((contentId = null) => {
        clearScheduledFlush();
        if (!pendingProgressRef.current) return;
        if (contentId == null || String(pendingProgressRef.current.contentId) === String(contentId)) {
            pendingProgressRef.current = null;
        }
    }, [clearScheduledFlush]);

    const scheduleFlush = useCallback(() => {
        clearScheduledFlush();
        flushTimerRef.current = window.setTimeout(() => flushPendingProgress(), 4000);
    }, [clearScheduledFlush, flushPendingProgress]);

    /* ── Activate content ── */
    const activateContent = useCallback((content) => {
        if (!content) return;
        flushPendingProgress();
        dispatch(setCurrentLearnerContent(content.id));
        if (!content.locked) {
            trackingSessionRef.current = { contentId: content.id, sessionKey: createSessionKey(content.id), sequenceNumber: 0 };
            if (String(lastStartedContentIdRef.current) !== String(content.id)) {
                lastStartedContentIdRef.current = content.id;
                dispatch(startLearnerContentRequest(moduleId, content.id, buildSequencedPayload(content.id)));
            }
        }
    }, [buildSequencedPayload, dispatch, flushPendingProgress, moduleId]);

    /* ── Current content ── */
    const allContents = useMemo(() => flattenContents(sections), [sections]);

    const currentContent = useMemo(() => {
        return allContents.find((c) => String(c.id) === String(currentContentId)) || null;
    }, [allContents, currentContentId]);

    const currentIndex = useMemo(() => {
        return allContents.findIndex((c) => String(c.id) === String(currentContentId));
    }, [allContents, currentContentId]);

    useEffect(() => {
        currentContentRef.current = currentContent;
        if (String(currentContent?.progress?.status || "").toUpperCase() === "COMPLETED") {
            lastCompletedContentIdRef.current = currentContent?.id;
        }
    }, [currentContent]);

    useEffect(() => {
        const nextMap = new Map();
        allContents.forEach((c) => nextMap.set(String(c.id), c.progress || {}));
        contentProgressByIdRef.current = nextMap;
    }, [allContents]);

    /* ── Auto-select first content ── */
    useEffect(() => {
        if (!player || !sections?.length) return;
        if (!allContents.length) return;
        const selected = allContents.find((c) => String(c.id) === String(currentContentId));
        if (selected && !selected.locked) return;
        const preferred = allContents.find((c) => String(c.id) === String(player.lastContentId) && !c.locked) || findFirstVisibleContent(sections);
        if (preferred && String(preferred.id) !== String(currentContentId)) {
            activateContent(preferred);
        }
    }, [activateContent, allContents, currentContentId, player, sections]);

    /* ── Track content ── */
    const handleTrackContent = useCallback((update = {}) => {
        const active = currentContentRef.current;
        if (!active || active.locked) return;
        if (String(active.progress?.status || "").toUpperCase() === "COMPLETED") {
            lastCompletedContentIdRef.current = active.id;
            return;
        }
        if (pendingProgressRef.current && String(pendingProgressRef.current.contentId) !== String(active.id)) {
            flushPendingProgress();
        }
        const cur = pendingProgressRef.current || {
            contentId: active.id, timeSpentDeltaSeconds: 0,
            completionPercent: Number(active.progress?.progressPercent || 0),
            progressPayload: { ...(active.progress?.progressPayload || {}) },
        };
        const next = {
            contentId: active.id,
            timeSpentDeltaSeconds: Number(cur.timeSpentDeltaSeconds || 0) + Number(update.timeSpentDeltaSeconds || 0),
            completionPercent: Math.max(Number(cur.completionPercent || 0), Number(update.completionPercent || 0)),
            progressPayload: { ...(cur.progressPayload || {}), ...(update.progressPayload || {}) },
        };
        pendingProgressRef.current = next;
        if (update.immediate) { flushPendingProgress(); } else { scheduleFlush(); }
    }, [flushPendingProgress, scheduleFlush]);

    const handleCompleteContent = useCallback((content) => {
        if (!content || String(content.progress?.status || "").toUpperCase() === "COMPLETED") return;
        if (String(lastCompletedContentIdRef.current) === String(content.id)) return;
        lastCompletedContentIdRef.current = content.id;
        discardPendingProgress(content.id);
        dispatch(completeLearnerContentRequest(moduleId, content.id, buildSequencedPayload(content.id)));
    }, [buildSequencedPayload, discardPendingProgress, dispatch, moduleId]);

    const handleRefreshPlayer = useCallback(() => {
        if (!moduleId) return;
        dispatch(fetchModulePlayerRequest(moduleId));
    }, [dispatch, moduleId]);

    /* ── Cleanup ── */
    useEffect(() => () => { clearScheduledFlush(); flushPendingProgress(); }, [clearScheduledFlush, flushPendingProgress]);

    useEffect(() => {
        const flush = () => flushPendingProgress();
        const visChange = () => { if (document.hidden) flushPendingProgress(); };
        window.addEventListener("beforeunload", flush);
        document.addEventListener("visibilitychange", visChange);
        return () => { window.removeEventListener("beforeunload", flush); document.removeEventListener("visibilitychange", visChange); };
    }, [flushPendingProgress]);

    /* ── Navigation ── */
    const goTo = (dir) => {
        const nextIdx = currentIndex + dir;
        if (nextIdx >= 0 && nextIdx < allContents.length) {
            const target = allContents[nextIdx];
            if (!target.locked) activateContent(target);
        }
    };

    /* ── Progress calculation ── */
    const progressPercent = player?.progress?.progressPercent || 0;
    const completedRequired = player?.progress?.completedRequiredContentsCount || 0;
    const totalRequired = player?.progress?.totalRequiredContentsCount || 0;

    /* ── Close with animation ── */
    const handleClose = () => {
        flushPendingProgress();
        setExiting(true);
        setTimeout(() => {
            onClose();
            // If they closed normally and it happens to be finished, also trigger step completion
            const isCompletedModule = progressPercent === 100 || (totalRequired > 0 && completedRequired >= totalRequired);
            if (isCompletedModule && onCompleteStep) {
                onCompleteStep();
            }
        }, 350);
    };

    const handleFinishMission = () => {
        flushPendingProgress();
        const isCompletedModule = progressPercent === 100 || (totalRequired > 0 && completedRequired >= totalRequired);
        
        if (isCompletedModule) {
            setExiting(true);
            setTimeout(() => {
                onClose();
                if (onCompleteStep) {
                    onCompleteStep();
                }
            }, 350);
        } else {
            Swal.fire({
                icon: 'warning',
                title: 'Mission incomplète',
                text: `Il vous reste des contenus obligatoires à terminer (${completedRequired}/${totalRequired} complétés) pour pouvoir valider cette mission.`,
                confirmButtonColor: '#ee645f'
            });
        }
    };

    /* ── Render ── */
    return (
        <div className={`omp-wrapper ${exiting ? 'omp-exiting' : ''}`}>
            {/* ── Header ── */}
            <div className="omp-header">
                <div className="omp-header-left">
                    <button className="omp-back-btn" onClick={handleClose}>
                        <ArrowLeft size={16} />
                        <span>Retour</span>
                    </button>
                </div>

                <div className="omp-header-center">
                    <span className="omp-mission-label">
                        Mission {String(missionIndex + 1).padStart(2, '0')}
                    </span>
                    <span className="omp-mission-title">{missionTitle || player?.nom || 'Module'}</span>
                </div>

                <div className="omp-header-right">
                </div>
            </div>

            <div className="omp-layout">
                {/* ── Sidebar ── */}
                <div className={`omp-sidebar ${sidebarOpen ? 'open' : ''}`} style={sidebarOpen ? {} : { width: 0, padding: 0, border: 'none', overflow: 'hidden' }}>
                    <div className="omp-sidebar-header">
                        <span className="omp-sidebar-title">Sommaire</span>
                        <button className="omp-sidebar-toggle" onClick={() => setSidebarOpen(false)} title="Fermer le sommaire">
                            <PanelLeftClose size={18} />
                        </button>
                    </div>

                    <div className="omp-sidebar-scroll">
                        {(sections || []).map((section) => {
                            const isOpen = openSections[section.id] !== false;
                            const contents = section.contents || [];
                            const completedCount = contents.filter(c => String(c.progress?.status || "").toUpperCase() === "COMPLETED").length;

                            return (
                                <div className="omp-section" key={section.id}>
                                    <div className="omp-section-header" onClick={() => setOpenSections(prev => ({ ...prev, [section.id]: !isOpen }))}>
                                        <ChevronRight size={14} className={`omp-section-chevron ${isOpen ? 'open' : ''}`} />
                                        <span className="omp-section-name">{section.title || section.nom}</span>
                                        <span className="omp-section-count">{completedCount}/{contents.length}</span>
                                    </div>

                                    {isOpen && contents.map((content) => {
                                        const isActive = String(content.id) === String(currentContentId);
                                        const isCompleted = String(content.progress?.status || "").toUpperCase() === "COMPLETED";
                                        const isLocked = content.locked;

                                        let itemClass = "omp-content-item";
                                        if (isActive) itemClass += " active";
                                        if (isCompleted) itemClass += " completed";
                                        if (isLocked) itemClass += " locked";

                                        return (
                                            <div
                                                key={content.id}
                                                className={itemClass}
                                                onClick={() => !isLocked && activateContent(content)}
                                            >
                                                <span className="omp-content-type-icon">{getContentTypeIcon(content.contentType)}</span>
                                                {isCompleted ? (
                                                    <CheckCircle size={14} className="omp-content-status" />
                                                ) : isLocked ? (
                                                    <Lock size={14} className="omp-content-status" />
                                                ) : isActive ? (
                                                    <Play size={14} className="omp-content-status current" />
                                                ) : (
                                                    <div className="omp-content-status" style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)' }} />
                                                )}
                                                <span className="omp-content-name" title={content.title || content.nom}>{content.title || content.nom}</span>
                                                {content.required && !isCompleted && <span className="omp-content-required">Requis</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                    <div className="omp-sidebar-footer">
                        <div className="omp-progress-label">
                            <span>Progression</span>
                            <span className="omp-progress-value">{progressPercent}%</span>
                        </div>
                        <div className="omp-progress-bar">
                            <div className="omp-progress-fill" style={{ width: `${progressPercent}%` }} />
                        </div>
                    </div>
                </div>

                {/* ── Main Content ── */}
                <div className="omp-main">
                    {!sidebarOpen && (
                        <button
                            className="omp-sidebar-toggle"
                            style={{ position: 'absolute', top: 12, left: 12, zIndex: 5, background: 'rgba(255,255,255,0.06)', padding: '8px', borderRadius: '8px' }}
                            onClick={() => setSidebarOpen(true)}
                            title="Ouvrir le sommaire"
                        >
                            <PanelLeft size={18} />
                        </button>
                    )}

                    <div className="omp-content-viewer">
                        {loading && !player ? (
                            <div className="omp-state">
                                <div className="omp-loader" />
                                <span>Chargement du module...</span>
                            </div>
                        ) : error && !player ? (
                            <div className="omp-state">
                                <span>❌ {error}</span>
                            </div>
                        ) : !currentContent ? (
                            <div className="omp-state">
                                <BookOpen size={32} />
                                <span>Aucun contenu disponible dans ce module.</span>
                            </div>
                        ) : (
                            <div className="omp-viewer-inner">
                                <ContentRenderer
                                    key={currentContent.id}
                                    moduleItem={player}
                                    content={currentContent}
                                    onComplete={() => handleCompleteContent(currentContent)}
                                    onTrack={handleTrackContent}
                                    onRefreshPlayer={handleRefreshPlayer}
                                    hideHeader={false}
                                    saveStatus={String(saveContentId) === String(currentContent.id) ? saveStatus : "idle"}
                                    saveError={String(saveContentId) === String(currentContent.id) ? saveError : null}
                                />
                            </div>
                        )}
                    </div>

                    {/* ── Bottom Navigation ── */}
                    {currentContent && (
                        <div className="omp-nav-bar">
                            <button
                                className="omp-nav-btn"
                                disabled={currentIndex <= 0}
                                onClick={() => goTo(-1)}
                            >
                                <ArrowLeft size={16} />
                                <span>Précédent</span>
                            </button>

                            <span className="omp-nav-center">
                                {currentIndex + 1} / {allContents.length}
                            </span>

                            {currentIndex < allContents.length - 1 ? (
                                <button
                                    className="omp-nav-btn primary"
                                    disabled={allContents[currentIndex + 1]?.locked}
                                    onClick={() => goTo(1)}
                                >
                                    <span>Suivant</span>
                                    <ArrowRight size={16} />
                                </button>
                            ) : (
                                <div style={{ width: 100 }} /> /* Spacer to balance the layout */
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
